/**
 * Magic Link Authentication Component
 * Provides passwordless authentication via email magic links
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface MagicLinkLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  redirectTo?: string;
  standalone?: boolean;
}

interface MagicLinkState {
  email: string;
  isLoading: boolean;
  isLinkSent: boolean;
  countdown: number;
  verifying: boolean;
}

interface ValidationErrors {
  email?: string;
  general?: string;
}

interface MagicLinkResponse {
  success: boolean;
  message: string;
  error?: string;
}

interface VerifyResponse {
  success: boolean;
  message: string;
  data?: {
    user: any;
    token: string;
  };
  error?: string;
}

export const MagicLinkLogin: React.FC<MagicLinkLoginProps> = ({
  onSuccess,
  onError,
  redirectTo,
  standalone = false
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<MagicLinkState>({
    email: '',
    isLoading: false,
    isLinkSent: false,
    countdown: 0,
    verifying: false
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Check for magic link verification on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkVerification(token);
    }
  }, [searchParams]);

  // Countdown timer for resend functionality
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (formState.countdown > 0) {
      timer = setTimeout(() => {
        setFormState(prev => ({ ...prev, countdown: prev.countdown - 1 }));
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [formState.countdown]);

  // Auto-focus email input
  useEffect(() => {
    if (!formState.isLinkSent && emailRef.current) {
      emailRef.current.focus();
    }
  }, [formState.isLinkSent]);

  /**
   * Validate email format
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formState.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formState.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Get API base URL based on environment
   */
  const getApiBaseUrl = (): string => {
    if (import.meta.env.PROD) {
      return 'https://telemetry.briefcasebrain.com';
    }
    return 'http://localhost:3002';
  };

  /**
   * Request magic link from backend
   */
  const requestMagicLink = async (email: string): Promise<MagicLinkResponse> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/magic-auth/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Magic link request error:', error);
      return {
        success: false,
        message: 'Failed to send magic link',
        error: error.message || 'Network error',
      };
    }
  };

  /**
   * Verify magic link token with backend
   */
  const verifyMagicLink = async (token: string): Promise<VerifyResponse> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/magic-auth/verify?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Magic link verification error:', error);
      return {
        success: false,
        message: 'Failed to verify magic link',
        error: error.message || 'Network error',
      };
    }
  };

  /**
   * Handle magic link verification
   */
  const handleMagicLinkVerification = async (token: string): Promise<void> => {
    setFormState(prev => ({ ...prev, verifying: true }));
    setValidationErrors({});

    try {
      const response = await verifyMagicLink(token);

      if (response.success && response.data) {
        // Store auth data
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('auth_user', JSON.stringify(response.data.user));

        // Call success handler
        if (onSuccess) {
          onSuccess();
        }

        // Redirect or navigate
        if (redirectTo) {
          window.location.href = redirectTo;
        } else if (standalone) {
          navigate('/');
        }
      } else {
        const errorMessage = response.error || 'Magic link verification failed';
        setValidationErrors({ general: errorMessage });

        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'An error occurred during verification';
      setValidationErrors({ general: errorMessage });

      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setFormState(prev => ({ ...prev, verifying: false }));
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormState(prev => ({ ...prev, isLoading: true }));
    setValidationErrors({});

    try {
      const response = await requestMagicLink(formState.email.toLowerCase().trim());

      if (response.success) {
        setFormState(prev => ({
          ...prev,
          isLoading: false,
          isLinkSent: true,
          countdown: 60, // 60 second cooldown
        }));
      } else {
        setFormState(prev => ({ ...prev, isLoading: false }));
        const errorMessage = response.error || response.message || 'Failed to send magic link';
        setValidationErrors({ general: errorMessage });

        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (error: any) {
      setFormState(prev => ({ ...prev, isLoading: false }));
      const errorMessage = error.message || 'An error occurred';
      setValidationErrors({ general: errorMessage });

      if (onError) {
        onError(errorMessage);
      }
    }
  };

  /**
   * Handle email input change
   */
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value.replace(/[<>]/g, ''); // Basic XSS prevention
    setFormState(prev => ({ ...prev, email: value }));

    // Clear email error
    if (validationErrors.email) {
      setValidationErrors(prev => ({ ...prev, email: undefined }));
    }
  };

  /**
   * Resend magic link
   */
  const handleResend = (): void => {
    setFormState(prev => ({
      ...prev,
      isLinkSent: false,
      countdown: 0,
    }));
  };

  if (formState.verifying) {
    return (
      <div className="text-center space-y-6">
        <div className="mb-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          Verifying Magic Link
        </h2>
        <p className="text-slate-700">
          Please wait while we verify your authentication...
        </p>
      </div>
    );
  }

  if (formState.isLinkSent) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Check Your Email</h2>
          <p className="text-slate-700">
            We've sent a magic link to <strong className="text-blue-600">{formState.email}</strong>
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-blue-800 text-sm">
              Click the link in your email to sign in instantly. The link will expire in 10 minutes.
            </p>
          </div>

          <div className="text-center">
            <p className="text-sm text-slate-600 mb-3">
              Didn't receive the email? Check your spam folder or
            </p>

            {formState.countdown > 0 ? (
              <p className="text-sm text-slate-500">
                Resend in {formState.countdown} seconds
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="text-blue-600 hover:text-blue-500 text-sm font-medium underline"
              >
                Send another magic link
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500">
            Magic links provide secure, passwordless authentication
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* General Error */}
        {validationErrors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{validationErrors.general}</p>
          </div>
        )}

        {/* Email Field */}
        <div>
          <label htmlFor="magic-email" className="block text-sm font-medium text-slate-700 mb-2">
            Email Address
          </label>
          <input
            ref={emailRef}
            type="email"
            id="magic-email"
            name="email"
            value={formState.email}
            onChange={handleEmailChange}
            className={`w-full px-4 py-3 border rounded-md text-slate-900 placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
              validationErrors.email ? 'border-red-300' : 'border-slate-300'
            }`}
            placeholder="you@company.com"
            autoComplete="email"
            disabled={formState.isLoading}
            aria-describedby={validationErrors.email ? 'magic-email-error' : undefined}
            required
          />
          {validationErrors.email && (
            <p id="magic-email-error" className="text-red-600 text-sm mt-1">
              {validationErrors.email}
            </p>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p style={{ color: 'rgb(148, 163, 184)', fontSize: '14px' }}>
              No password needed - we'll send you a secure link to sign in instantly
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={formState.isLoading}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
        >
          {formState.isLoading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending Magic Link...
            </div>
          ) : (
            'Send Magic Link'
          )}
        </button>
      </form>
    </div>
  );
};

export default MagicLinkLogin;